import { memo, useState } from "react";
import { useNavigate } from "react-router";
import { Avatar, Button, Drawer, Empty, Flex, Skeleton, Tag, Typography } from "antd";
import { EnvironmentOutlined, PlusOutlined, RightOutlined } from "@ant-design/icons";

import LazyJoinTheMomentModal from "@/components/joinMoment/LazyJoinTheMomentModal";
import T from "@/components/common/T";
import useThemeWalks from "./useThemeWalks";
import { BRAND, BRAND_GRADIENT, NAVY } from "./constants";
import { useGlobalStore } from "@/context/useGlobalStore";
import useSessionStore from "@/hooks/useSessionStore";
import { getEventDetailsRoute } from "@/utils/eventPage/helpers";

const { Text } = Typography;

// Full-bleed theme banner spanning the whole drawer width (body padding is
// zeroed on the Drawer, so this sits edge-to-edge).
const ThemeBanner = ({ src, alt }) =>
    src ? (
        <img
            src={src}
            alt={alt}
            style={{ display: "block", width: "100%", height: 150, objectFit: "cover" }}
        />
    ) : (
        <div
            role="img"
            aria-label={alt}
            style={{
                width: "100%", height: 150, display: "grid", placeItems: "center",
                background: BRAND_GRADIENT, color: "rgba(255,255,255,.9)", fontSize: 40,
            }}
        >
            <EnvironmentOutlined />
        </div>
    );

// Side drawer listing the walks for a theme near the visitor's location.
// Data is fetched from `walks-by-theme-location` when the drawer is open.
const ThemeWalksDrawer = ({ theme, open, onClose, onCreate }) => {
    const { data, isLoading, isError, refetch } = useThemeWalks(theme?.id, open);
    const navigateTo = useNavigate();
    const [joinSignal, setJoinSignal] = useState(0);
    const walkerIPDetails = useGlobalStore((s) => s.walkerIPDetails);
    const groupMemberIPDetails = useGlobalStore((s) => s.groupMemberIPDetails);
    const setState = useGlobalStore((s) => s.setState);
    const getStateSession = useSessionStore((s) => s.getState);

    const isWalkAlreadyJoined = (walk) => {
        if (walk?.joinType === "group-walks") {
            return (
                groupMemberIPDetails?.joined_group_walks?.map(({ group_walk_id }) => Number(group_walk_id))?.includes(Number(walk?.id)) ||
                getStateSession("myGroupWalks")?.includes(walk?.id) ||
                false
            );
        }
        return (
            walkerIPDetails?.joined_walks?.map(({ walk_id }) => Number(walk_id))?.includes(Number(walk?.id)) ||
            getStateSession("myWalks")?.includes(walk?.id) ||
            false
        );
    };

    const walks = data?.walks ?? [];
    const count = data?.count ?? 0;
    const title = data?.theme?.name || theme?.title;
    const bannerImage = data?.theme?.image_url || theme?.image;
    const locationLabel = data?.locationLabel;

    // Header-level quick action — joins the nearest not-yet-joined walk in
    // the list without requiring the visitor to scroll to a specific row.
    const nextJoinableWalk = walks.find((walk) => !isWalkAlreadyJoined(walk));

    // Mirrors ThemeCard's own "Create" pill: closes this drawer and hands off
    // to the create-walk flow, preselecting this theme.
    const handleCreateWalk = () => {
        onCreate?.({ walk_theme_id: theme?.id, walk_theme_name: theme?.title, is_join_walk: 1 });
    };

    return (
        <Drawer
            title={title}
            placement="right"
            size={420}
            open={open}
            onClose={onClose}
            styles={{ body: { padding: 0 } }}
            zIndex={1050}
            extra={
                <Flex align="center" gap={8}>
                    <Button
                        size="small"
                        shape="round"
                        icon={<PlusOutlined />}
                        onClick={handleCreateWalk}
                        style={{ background: BRAND_GRADIENT, color: NAVY, border: "none", fontWeight: 700 }}
                    >
                        <T k="Join Walk" />
                    </Button>
                </Flex>
            }
        >
            <ThemeBanner src={bannerImage} alt={title} />

            <div style={{ padding: "16px 24px 24px" }}>
            {!isLoading && !isError && (
                <Flex align="center" justify="space-between" gap={8} wrap style={{ marginBottom: 16 }}>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                        {locationLabel ? (
                            <>
                                <EnvironmentOutlined style={{ color: BRAND, marginInlineEnd: 4 }} />
                                <T k="Near" /> {locationLabel}
                            </>
                        ) : (
                            <T k="Walks in this theme" />
                        )}
                    </Text>
                    <Tag style={{ borderRadius: 999, border: "none", background: BRAND, color: NAVY, fontWeight: 700 }}>
                        {count} <T k={count === 1 ? "walk" : "walks"} />
                    </Tag>
                </Flex>
            )}

            {nextJoinableWalk && (
                <LazyJoinTheMomentModal
                    hideTrigger
                    openSignal={joinSignal}
                    details={{ ...nextJoinableWalk, walk_theme_id: theme?.id }}
                    type={nextJoinableWalk.joinType}
                    onWalkJoined={onClose}
                />
            )}

            {isLoading ? (
                <Skeleton active paragraph={{ rows: 6 }} />
            ) : isError ? (
                <Empty description={<T k="Couldn't load walks." />}>
                    <a onClick={() => refetch()} style={{ color: BRAND, fontWeight: 600, cursor: "pointer" }}>
                        <T k="Try again" />
                    </a>
                </Empty>
            ) : walks.length === 0 ? (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    style={{ padding: "24px 0" }}
                    description={
                        <Flex vertical gap={2} align="center">
                            <Text strong>
                                <T k="No walks yet for" /> "{title}"{locationLabel ? <> <T k="near you" /></> : ""}.
                            </Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                <T k="Be the first to start one!" />
                            </Text>
                        </Flex>
                    }
                >
                    <Button
                        icon={<PlusOutlined />}
                        onClick={handleCreateWalk}
                        style={{ background: BRAND_GRADIENT, color: NAVY, border: "none", fontWeight: 700 }}
                    >
                        <T k="Create a walk for this theme" />
                    </Button>
                </Empty>
            ) : (
                walks.map((walk) => {
                    const alreadyJoined = isWalkAlreadyJoined(walk);
                    const openWalkPage = () => {
                        // Close both this theme drawer and the outer "Join a Walk"
                        // drawer so the destination walk/group-walk page is visible.
                        onClose?.();
                        setState("joinWalkDrawerClose", Date.now());
                        navigateTo(getEventDetailsRoute(walk.joinType, walk.id));
                    };
                    return (
                        <div key={walk.id} className="jw-walk-row">
                            <Flex align="center" gap={10} style={{ minWidth: 0, cursor: "pointer" }} onClick={openWalkPage}>
                                <Avatar src={walk.image || undefined} icon={<EnvironmentOutlined />} size={40} shape="circle" />
                                <Flex vertical style={{ minWidth: 0 }}>
                                    <Text strong ellipsis style={{ fontSize: 13 }}>{walk.name}</Text>
                                    {walk.hostedBy && (
                                        <Text type="secondary" ellipsis style={{ fontSize: 11 }}>
                                            <T k="by" /> {walk.hostedBy}
                                        </Text>
                                    )}
                                    <Text type="secondary" ellipsis style={{ fontSize: 12 }}>
                                        <EnvironmentOutlined style={{ color: BRAND, marginInlineEnd: 4 }} />
                                        {walk.location}
                                    </Text>
                                </Flex>
                            </Flex>
                            <LazyJoinTheMomentModal
                                details={{ ...walk, walk_theme_id: theme?.id }}
                                type={walk.joinType}
                                isAlreadyJoined={alreadyJoined}
                                onWalkJoined={onClose}
                                joinBtnProps={{
                                    children: alreadyJoined ? <T k="Joined" /> : <T k="Join" />,
                                    size: "small",
                                    shape: "round",
                                    icon: <RightOutlined />,
                                    iconPlacement: "end",
                                    disabled: alreadyJoined,
                                    style: { background: BRAND_GRADIENT, color: NAVY, border: "none", fontWeight: 700 },
                                }}
                            />
                        </div>
                    );
                })
            )}
            </div>
        </Drawer>
    );
};

export default memo(ThemeWalksDrawer);
